/**
 * Social channel URL detection & ID extraction.
 *
 * Shared by the "Add influencer" page (src/pages/influencers/add.tsx) and the
 * Tampermonkey crawler userscript (src/tampermonkey/platform.ts) so both
 * resolve channel URLs identically across all 13 supported platforms.
 */

export type SiteId =
  | 'youtube'
  | 'twitch'
  | 'facebook'
  | 'instagram'
  | 'twitter'
  | 'tiktok'
  | 'douyin'
  | 'steam_curator'
  | 'chzzk'
  | 'discord'
  | 'telegram'
  | 'bilibili'
  | 'afreeca';

const SITE_PATTERNS: Record<SiteId, RegExp | ((url: string) => boolean)> = {
  youtube: /^https?:\/\/(www\.)?youtube\.com/,
  twitch: /^https?:\/\/(www\.)?twitch\.tv/,
  facebook: /^https?:\/\/(www\.)?facebook\.com/,
  instagram: /^https?:\/\/(www\.)?instagram\.com/,
  twitter: (url: string) => /^https?:\/\/(www\.)?(twitter\.com|x\.com)/.test(url),
  tiktok: /^https?:\/\/(www\.)?tiktok\.com/,
  douyin: /^https?:\/\/(www\.)?douyin\.com/,
  steam_curator: /^https?:\/\/store\.steampowered\.com/,
  chzzk: /^https?:\/\/(www\.)?chzzk\.naver\.com/,
  discord: /^https?:\/\/(www\.)?discord\.(com|gg)/,
  telegram: /^https?:\/\/(www\.)?t\.me/,
  bilibili: /^https?:\/\/(www\.|space\.)?bilibili\.com/,
  // SOOP (formerly AfreecaTV): accept all known domain/subdomain forms.
  // soop.co.kr, (www.)sooplive.com, ch.sooplive.co.kr, bj./play.afreecatv.com
  afreeca: /^https?:\/\/(?:www\.|ch\.|bj\.|play\.)?(?:soop\.co\.kr|sooplive\.(?:com|co\.kr)|afreecatv\.com)/,
};

/** Supported channel/social-link URL examples, one per `SiteId` (canonical
 *  subdomain per platform). Shared by the "Add influencer by individual" dialog's
 *  social-link hint and the toolbar's registration-check popover. */
export const SOCIAL_LINK_EXAMPLES: Record<SiteId, string> = {
  youtube: 'https://www.youtube.com/@handle',
  twitch: 'https://www.twitch.tv/name',
  tiktok: 'https://www.tiktok.com/@name',
  instagram: 'https://www.instagram.com/name',
  twitter: 'https://x.com/name',
  facebook: 'https://www.facebook.com/name',
  douyin: 'https://www.douyin.com/user/id',
  steam_curator: 'https://store.steampowered.com/curator/name',
  chzzk: 'https://chzzk.naver.com/id',
  bilibili: 'https://space.bilibili.com/id',
  telegram: 'https://t.me/name',
  discord: 'https://discord.gg/invite',
  afreeca: 'https://ch.sooplive.co.kr/id',
};

export function detectSite(url: string): SiteId | null {
  for (const [name, check] of Object.entries(SITE_PATTERNS) as [SiteId, RegExp | ((u: string) => boolean)][]) {
    if (typeof check === 'function' ? check(url) : check.test(url)) return name;
  }
  return null;
}

export interface ParsedChannelUrl {
  site: SiteId | null;
  id: string | null;
  type: string | null;
}

/**
 * Parses a raw URL into `{ site, id, type }` via `detectSite` + `extractId` — the
 * single shared entry point every caller (the "Add influencer by individual" dialog,
 * the toolbar's registration-check popover, ...) should use instead of composing the
 * two calls itself, so a future fix to either detection step stays in sync everywhere.
 */
export function parseChannelUrl(url: string): ParsedChannelUrl {
  const trimmed = url.trim();
  if (!trimmed) return { site: null, id: null, type: null };
  const site = detectSite(trimmed);
  if (!site) return { site: null, id: null, type: null };
  const { id, type } = extractId(site, trimmed);
  return { site, id, type };
}

export function extractId(site: SiteId, url: string): { id: string | null; type: string | null } {
  switch (site) {
    case 'youtube': {
      const channelMatch = url.match(/youtube\.com\/channel\/([^/?#]+)/);
      if (channelMatch) return { id: channelMatch[1], type: 'channel' };
      const userMatch = url.match(/youtube\.com\/(?:user\/|@)([^/?#]+)/);
      if (userMatch) return { id: userMatch[1], type: 'user' };
      const cMatch = url.match(/youtube\.com\/c\/([^/?#]+)/);
      if (cMatch) return { id: cMatch[1], type: 'user' };
      return { id: null, type: null };
    }
    case 'twitch': {
      const m = url.match(/twitch\.tv\/([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'instagram': {
      // Capture the FIRST path segment (the username), mirroring extractSocialId from the
      // original CrawlerPage hotfix (gc-mono 74f06088). The old regex mis-parsed profile
      // URLs with a trailing path like ".../kalitaku/reels/?hl=en" as "reels", which then
      // falsely matched an existing channel and reported it as already in the DB.
      const m = url.match(/instagram\.com\/+([a-zA-Z0-9-_.]+)/i);
      return { id: m?.[1]?.toLowerCase() ?? null, type: null };
    }
    case 'tiktok': {
      const m = url.match(/tiktok\.com\/@([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'douyin': {
      const m = url.match(/douyin\.com\/user\/([^/?&#=]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'facebook': {
      // Exclude "&" too — profile.php?id=X&sk=... (multiple query params) otherwise
      // leaks the trailing params into the id.
      const m = url.match(/facebook\.com\/(?:profile\.php\?id=)?([^/?#&]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'twitter': {
      const m = url.match(/(?:twitter|x)\.com\/([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'steam_curator': {
      const m = url.match(/store\.steampowered\.com\/curator\/([^?/]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'chzzk': {
      const m = url.match(/chzzk\.naver\.com\/([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'discord': {
      const m = url.match(/discord\.(?:com|gg)\/(?:invite\/)?([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'telegram': {
      const m = url.match(/t\.me\/([^/?#]+)/);
      return { id: m?.[1] ?? null, type: null };
    }
    case 'bilibili': {
      // space.bilibili.com/{uid} (profile) takes priority; otherwise pull the id out
      // of bilibili.com/{number} or bilibili.com/video/{videoId} via capture groups
      // (previously used the whole match, which included the "bilibili.com/" prefix).
      const spaceMatch = url.match(/space\.bilibili\.com\/(\d+)/);
      if (spaceMatch) return { id: spaceMatch[1], type: null };
      const m = url.match(/bilibili\.com\/(?:(\d+)|video\/([^/?#]+))/);
      return { id: m?.[1] ?? m?.[2] ?? null, type: null };
    }
    case 'afreeca': {
      // sooplive.com/station/<id> (new global form) → station name is the id
      const station = url.match(/sooplive\.com\/station\/([^/?#]+)/i);
      if (station) return { id: station[1], type: null };
      // ch.sooplive.co.kr/<id>, soop.co.kr/<id>, sooplive.com/<id>, bj./play.afreecatv.com/<id>
      const m = url.match(/(?:soop\.co\.kr|sooplive\.(?:com|co\.kr)|bj\.afreecatv\.com|play\.afreecatv\.com)\/([^/?#]+)/i);
      return { id: m?.[1] ?? null, type: null };
    }
    default:
      return { id: null, type: null };
  }
}
